// screens-calendar.jsx — Kalender (month view) + Lediga tider (free-slot finder)
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
import { Stat, JobCard, Empty } from './screens-main.jsx';
const { useState: useStateK, useMemo: useMemoK, useEffect: useEffectK, useRef: useRefK } = React;

const K_MONTHS = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const K_WD = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

function kAddDays(iso, n) { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n); return isoLocal(d); }
function kJobHours(j) { return (j.estMinutes || 0) / 60; }
function hm(min) { return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`; }
function kWeekdayLong(iso) {
  const d = new Date(iso + 'T00:00:00');
  const w = d.toLocaleDateString('sv-SE', { weekday: 'long' });
  return w.charAt(0).toUpperCase() + w.slice(1);
}

// ── Swedish red days (helgdagar) ────────────────────────────
function easterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4,
    f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30,
    i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451),
    month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}
const _holCache = {};
function holidaySet(year) {
  if (_holCache[year]) return _holCache[year];
  const set = new Set();
  const add = (dt) => set.add(isoLocal(dt));
  add(new Date(year, 0, 1));   // Nyårsdagen
  add(new Date(year, 0, 6));   // Trettondedag jul
  const easter = easterSunday(year);
  const off = (n) => { const dt = new Date(easter); dt.setDate(dt.getDate() + n); return dt; };
  add(off(-2)); // Långfredag
  add(off(0));  // Påskdagen
  add(off(1));  // Annandag påsk
  add(off(39)); // Kristi himmelsfärdsdag
  add(off(49)); // Pingstdagen
  add(new Date(year, 4, 1));  // Första maj
  add(new Date(year, 5, 6));  // Nationaldagen
  // Midsommar: Saturday 20–26 June + the Friday before (afton, de facto)
  for (let dd = 20; dd <= 26; dd++) { const dt = new Date(year, 5, dd); if (dt.getDay() === 6) { add(dt); const fr = new Date(dt); fr.setDate(dt.getDate() - 1); add(fr); break; } }
  // Alla helgons dag: Saturday 31 Oct – 6 Nov
  for (let dd = 31; dd <= 37; dd++) { const dt = new Date(year, 9, dd); if (dt.getDay() === 6) { add(dt); break; } }
  add(new Date(year, 11, 24)); // Julafton (de facto)
  add(new Date(year, 11, 25)); // Juldagen
  add(new Date(year, 11, 26)); // Annandag jul
  add(new Date(year, 11, 31)); // Nyårsafton (de facto)
  _holCache[year] = set;
  return set;
}
function isRedDay(iso) { return holidaySet(Number(iso.slice(0, 4))).has(iso); }
function isWeekend(iso) { const w = new Date(iso + 'T00:00:00').getDay(); return w === 0 || w === 6; }

// ── KALENDER (month grid) ───────────────────────────────────
function CalendarScreen({ jobs, onOpen, onOpenProfile, onMove }) {
  const t0 = new Date(TODAY + 'T00:00:00');
  const [view, setView] = useStateK({ y: t0.getFullYear(), m: t0.getMonth() });
  const [sel, setSel] = useStateK(TODAY);

  // index jobs by day for the visible month
  const byDay = useMemoK(() => {
    const map = {};
    jobs.forEach(j => { (map[j.date] = map[j.date] || { count: 0, min: 0 }); map[j.date].count++; map[j.date].min += (j.estMinutes || 0); });
    return map;
  }, [jobs]);

  const first = new Date(view.y, view.m, 1);
  const startIdx = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const iso = (d) => `${view.y}-${String(view.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const cells = [];
  for (let i = 0; i < startIdx; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // month totals
  let monthCount = 0, monthMin = 0;
  for (let d = 1; d <= daysInMonth; d++) { const info = byDay[iso(d)]; if (info) { monthCount += info.count; monthMin += info.min; } }

  const prev = () => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
  const next = () => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });
  const goToday = () => { setView({ y: t0.getFullYear(), m: t0.getMonth() }); setSel(TODAY); };

  const dayJobs = jobs.filter(j => j.date === sel).sort((a, b) => a.startTime.localeCompare(b.startTime));
  const selInfo = byDay[sel];

  return (
    <>
      <AppHeader large title="Kalender" right={<ProfileButton onClick={onOpenProfile} />} />

      {/* month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px 12px', flexShrink: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 760, letterSpacing: '-.01em' }}>{K_MONTHS[view.m]} {view.y}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={goToday} className="gv-chip" style={{ height: 38 }}>Idag</button>
          <button className="gv-iconbtn" onClick={prev} aria-label="Föregående månad"><Icon name="chevL" size={20} stroke={2.2} /></button>
          <button className="gv-iconbtn" onClick={next} aria-label="Nästa månad"><Icon name="chevR" size={20} stroke={2.2} /></button>
        </div>
      </div>

      <div className="gv-scroll" style={{ paddingTop: 0 }}>
        {/* weekday header + grid */}
        <div className="gv-mcal" style={{ marginBottom: 4 }}>
          {K_WD.map(w => <div key={w} className="gv-mcal-wd">{w}</div>)}
        </div>
        <div className="gv-mcal">
          {cells.map((d, i) => {
            if (d == null) return <div key={'e' + i} />;
            const di = iso(d);
            const info = byDay[di];
            const h = info ? info.min / 60 : 0;
            const cls = ['gv-mcell'];
            if (isWeekend(di)) cls.push('is-weekend');
            if (di === TODAY) cls.push('is-today');
            if (di === sel) cls.push('is-sel');
            return (
              <button key={d} className={cls.join(' ')} onClick={() => setSel(di)}>
                <span className="num">{d}</span>
                {info && <>
                  <span className="bars">{Array.from({ length: Math.min(info.count, 4) }).map((_, k) => <i key={k} />)}</span>
                  <span className="load">{num(h, h % 1 ? 1 : 0)}t</span>
                </>}
              </button>
            );
          })}
        </div>

        {/* month summary */}
        <Card style={{ padding: 0, marginTop: 14, overflow: 'hidden' }}>
          <div style={{ display: 'flex' }}>
            <Stat label={`Uppdrag i ${K_MONTHS[view.m].toLowerCase()}`} value={monthCount} />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <Stat label="Planerad tid" value={fmtHM(monthMin)} suffix="tim" />
          </div>
        </Card>

        {/* selected day */}
        <SecLabel action={selInfo ? `${selInfo.count} st · ${num(selInfo.min / 60, 1)} tim` : null}>
          {sel === TODAY ? 'Idag' : kWeekdayLong(sel)} {new Date(sel + 'T00:00:00').getDate()} {K_MONTHS[new Date(sel + 'T00:00:00').getMonth()].toLowerCase()}
        </SecLabel>
        {dayJobs.length
          ? dayJobs.map(j => <JobCard key={j.id} job={j} onOpen={onOpen} onMove={onMove} />)
          : <Empty icon="calendar" title="Inga uppdrag" sub={'Inget inbokat denna dag.' + (isRedDay(sel) ? ' (Röd dag)' : '')} />}
      </div>
    </>
  );
}

// ── LEDIGA TIDER (free-slot finder) ─────────────────────────
const WORK_START = 7 * 60;   // 07:00
const WORK_END = 16 * 60;    // 16:00
const WORK_LEN = WORK_END - WORK_START;

function toMinHM(t) { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m; }

// Place a job of duration D inside a free gap [gs,ge], preferring to avoid the
// lunch window; only span lunch (absorbing its minutes) when it can't fit otherwise.
function placeInGap(gs, ge, D, lw) {
  gs = ceilMin(gs); // planned starts land on a 15-min boundary
  if (!lw || lw.e <= gs || lw.s >= ge) { // no lunch interaction
    return (ge - gs >= D) ? { start: gs, end: gs + D, lunchMin: 0 } : null;
  }
  if (lw.s - gs >= D) return { start: gs, end: gs + D, lunchMin: 0 };            // fits before lunch
  const after = ceilMin(Math.max(gs, lw.e));
  if (ge - after >= D) return { start: after, end: after + D, lunchMin: 0 };     // fits after lunch
  if (ge - gs >= D + lw.len) return { start: gs, end: gs + D + lw.len, lunchMin: lw.len }; // span lunch
  return null;
}

function findFreeSlots(jobs, durationMin, includeOff, settings = {}, horizonDays = 35, maxResults = 6) {
  const lw = lunchWindow(settings);
  const travel = settings.travelOn === false ? 0 : (settings.travelDefault != null ? settings.travelDefault : 15);

  const out = [];
  for (let i = 0; i < horizonDays && out.length < maxResults; i++) {
    const di = kAddDays(TODAY, i);
    const off = isWeekend(di) || isRedDay(di);
    if (off && !includeOff) continue;

    // busy = existing jobs only; lunch is handled softly by placeInGap
    const items = jobs.filter(j => j.date === di && j.startTime)
      .map(j => { const s = toMinHM(j.startTime); return { s, e: s + (j.estMinutes || 0) }; })
      .sort((a, b) => a.s - b.s);

    // free gaps inside the work window; reserve travel time after each job
    const gaps = [];
    let c = WORK_START;
    let afterJob = false;
    for (const it of items) {
      const gapStart = c + (afterJob ? travel : 0);
      if (it.s > gapStart) gaps.push({ s: gapStart, e: Math.min(it.s, WORK_END), travel: afterJob && travel > 0 });
      if (it.e > c) { c = it.e; afterJob = true; }
      if (c >= WORK_END) break;
    }
    if (c < WORK_END) gaps.push({ s: c + (afterJob ? travel : 0), e: WORK_END, travel: afterJob && travel > 0 });

    let placed = null, gapTravel = false;
    for (const g of gaps) { const p = placeInGap(g.s, g.e, durationMin, lw); if (p) { placed = p; gapTravel = g.travel; break; } }
    if (!placed) continue;

    const freeMin = gaps.reduce((s, g) => s + Math.max(0, g.e - g.s), 0);
    out.push({ iso: di, start: placed.start, end: placed.end, freeMin, booked: items.length, off, travel: gapTravel, travelMin: gapTravel ? travel : 0, lunchMin: placed.lunchMin });
  }
  return out;
}

// ── DAY TIMELINE (drag the block to pick a start time) ──────
const TL_STRIPE = 'repeating-linear-gradient(135deg, rgba(176,125,26,.18) 0 5px, rgba(176,125,26,.06) 5px 10px)';
function DayTimeline({ iso, dayJobs, durationMin, settings, onBook }) {
  const WS = WORK_START, WE = WORK_END, span = WE - WS;
  const lw = lunchWindow(settings);
  const booked = useMemoK(() => dayJobs.filter(j => j.startTime)
    .map(j => { const s = hmToMin(j.startTime); return { s, e: s + (j.estMinutes || 0), title: j.title }; })
    .filter(b => b.s != null && b.e > WS && b.s < WE).sort((a, b) => a.s - b.s), [dayJobs]);
  const effLen = (t) => durationMin + lunchAbsorb(t, durationMin, settings);
  const validStarts = useMemoK(() => {
    const arr = [];
    for (let t = WS; t + durationMin <= WE; t += 15) {
      const eff = effLen(t);
      if (t + eff > WE) continue;
      if (booked.some(b => t < b.e && b.s < t + eff)) continue;
      arr.push(t);
    }
    return arr;
  }, [booked, durationMin, settings]);
  const nearest = (m, arr) => arr.reduce((best, v) => Math.abs(v - m) < Math.abs(best - m) ? v : best, arr[0]);
  const [start, setStart] = useStateK(validStarts.length ? validStarts[0] : null);
  useEffectK(() => {
    if (!validStarts.length) { setStart(null); return; }
    setStart(prev => (prev != null && validStarts.includes(prev)) ? prev : nearest(prev == null ? validStarts[0] : prev, validStarts));
  }, [validStarts]);

  const trackRef = useRefK(null);
  const [drag, setDrag] = useStateK(false);
  const pct = (m) => ((m - WS) / span) * 100;
  const moveTo = (clientX) => {
    const el = trackRef.current; if (!el || !validStarts.length) return;
    const r = el.getBoundingClientRect();
    const rel = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    setStart(nearest(WS + rel * span, validStarts));
  };
  const cx = (e) => e.clientX != null ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
  const onDown = (e) => {
    if (!validStarts.length) return;
    e.preventDefault(); setDrag(true); moveTo(cx(e));
    const mv = (ev) => moveTo(cx(ev));
    const up = () => { setDrag(false); window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up); window.removeEventListener('touchmove', mv); window.removeEventListener('touchend', up); };
    window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up);
    window.addEventListener('touchmove', mv, { passive: false }); window.addEventListener('touchend', up);
  };

  const d = new Date(iso + 'T00:00:00');
  const dayLabel = iso === TODAY ? 'Idag' : iso === kAddDays(TODAY, 1) ? 'Imorgon' : kWeekdayLong(iso);
  const eff = start != null ? effLen(start) : 0;
  const ticks = [];
  for (let h = 7; h <= 16; h++) ticks.push(h * 60);

  return (
    <Card style={{ padding: '12px 13px 13px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 9 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 720 }}>{dayLabel}</span>
          <span className="gv-tnum" style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{d.getDate()} {K_MONTHS[d.getMonth()].slice(0, 3).toLowerCase()}</span>
        </div>
        <span className="gv-tnum" style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{booked.length ? `${booked.length} inbokade` : 'ledig dag'}</span>
      </div>

      {/* track */}
      <div ref={trackRef} onPointerDown={onDown} style={{ position: 'relative', height: 50, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', touchAction: 'none', cursor: validStarts.length ? (drag ? 'grabbing' : 'grab') : 'default', userSelect: 'none' }}>
        {/* hour gridlines */}
        {ticks.map((t, i) => i === 0 ? null : <div key={t} style={{ position: 'absolute', left: pct(t) + '%', top: 0, bottom: 0, width: 1, background: 'var(--line)' }} />)}
        {/* lunch */}
        {lw && lw.e > WS && lw.s < WE && <div style={{ position: 'absolute', left: pct(Math.max(lw.s, WS)) + '%', width: (pct(Math.min(lw.e, WE)) - pct(Math.max(lw.s, WS))) + '%', top: 0, bottom: 0, background: TL_STRIPE }} />}
        {/* booked jobs */}
        {booked.map((b, i) => (
          <div key={i} style={{ position: 'absolute', left: pct(Math.max(b.s, WS)) + '%', width: (pct(Math.min(b.e, WE)) - pct(Math.max(b.s, WS))) + '%', top: 4, bottom: 4, background: 'rgba(63,110,163,.16)', border: '1px solid rgba(63,110,163,.35)', borderRadius: 6, display: 'flex', alignItems: 'center', padding: '0 6px', overflow: 'hidden' }}>
            <span style={{ fontSize: 10.5, fontWeight: 650, color: '#33578a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.title}</span>
          </div>
        ))}
        {/* draggable proposed block */}
        {start != null ? (
          <div style={{ position: 'absolute', left: pct(start) + '%', width: (pct(start + eff) - pct(start)) + '%', top: 2, bottom: 2, background: 'var(--accent)', borderRadius: 8, boxShadow: '0 3px 10px rgba(192,94,53,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, color: '#fff', minWidth: 0 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{hm(start)}</span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, marginLeft: 1 }}><i style={{ width: 2, height: 12, background: 'rgba(255,255,255,.7)', borderRadius: 2 }} /></span>
          </div>
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 650, color: 'var(--ink-3)' }}>Fullbokad dag</div>
        )}
      </div>
      {/* hour labels */}
      <div style={{ position: 'relative', height: 15, marginTop: 3 }}>
        {[7, 10, 13, 16].map(h => (
          <span key={h} className="gv-mono" style={{ position: 'absolute', left: pct(h * 60) + '%', transform: h === 16 ? 'translateX(-100%)' : (h === 7 ? 'none' : 'translateX(-50%)'), fontSize: 9.5, color: 'var(--ink-3)' }}>{String(h).padStart(2, '0')}</span>
        ))}
      </div>

      {start != null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 11 }}>
          <div style={{ minWidth: 0 }}>
            <div className="gv-tnum" style={{ fontSize: 15, fontWeight: 740 }}>{hm(start)}–{hm(start + eff)}</div>
            {eff > durationMin && <div style={{ fontSize: 11, color: 'var(--accent-ink)', fontWeight: 600, marginTop: 1 }}>inkl. {eff - durationMin} min lunch</div>}
          </div>
          <Btn variant="primary" size="sm" icon="plus" onClick={() => onBook(iso, hm(start))}>Boka {hm(start)}</Btn>
        </div>
      )}
    </Card>
  );
}

function FreeSlotsScreen({ jobs, onOpenProfile, onBook, settings = {} }) {
  const [hours, setHours] = useStateK(2);
  const [includeOff, setIncludeOff] = useStateK(false);
  const [view, setView] = useStateK('lista');
  const durationMin = Math.round(hours * 60);

  const tooLong = durationMin > WORK_LEN;
  const slots = useMemoK(() => tooLong ? [] : findFreeSlots(jobs, durationMin, includeOff, settings), [jobs, durationMin, includeOff, tooLong, settings]);

  return (
    <>
      <AppHeader large title="Lediga tider" right={<ProfileButton onClick={onOpenProfile} />} />
      <div className="gv-scroll" style={{ paddingTop: 2 }}>
        <Card style={{ background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="search" size={19} stroke={1.9} /></div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.45 }}>
            Ange hur lång tid uppdraget tar så hittar appen första lediga luckan i din kalender.
          </div>
        </Card>

        {/* duration */}
        <SecLabel>Beräknad tidsåtgång</SecLabel>
        <Card style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="clock" size={20} stroke={1.9} color="var(--ink-2)" />
            <span style={{ fontSize: 15, fontWeight: 650 }}>{num(hours, hours % 1 ? 1 : 0)} tim</span>
          </div>
          <Stepper value={hours} onChange={(v) => setHours(Math.max(0.5, v))} step={0.5} min={0.5} />
        </Card>

        {/* options */}
        <SecLabel>Sökregler</SecLabel>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div className="gv-lrow" style={{ justifyContent: 'space-between' }}>
            <div className="gv-lrow-tt" style={{ flex: 1 }}>
              <b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Inkludera helger & röda dagar</b>
              <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{includeOff ? 'Alla dagar i veckan' : 'Endast vardagar (mån–fre)'}</span>
            </div>
            <Toggle on={includeOff} onChange={setIncludeOff} />
          </div>
          <div className="gv-lrow">
            <div className="gv-lrow-ic"><Icon name="today" size={18} stroke={1.9} /></div>
            <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Arbetsdag</b><span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>07:00–16:00</span></div>
          </div>
          <div className="gv-lrow">
            <div className="gv-lrow-ic" style={settings.lunchOn ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name="sun" size={18} stroke={1.9} /></div>
            <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Lunch</b><span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{settings.lunchOn ? `${settings.lunchStart} \u00b7 ${settings.lunchMinutes} min \u2013 hålls ledig` : 'Avstängd'}</span></div>
          </div>
          <div className="gv-lrow" style={{ borderBottom: 'none' }}>
            <div className="gv-lrow-ic" style={(settings.travelOn !== false) ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name="car" size={18} stroke={1.9} /></div>
            <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 650, display: 'block' }}>Restid mellan uppdrag</b><span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{(settings.travelOn !== false) ? `Reserverar ~${settings.travelDefault} min efter varje uppdrag` : 'Avstängd'}</span></div>
          </div>
        </Card>

        {/* results */}
        <SecLabel action={slots.length ? `${slots.length} förslag` : null}>Föreslagna tider</SecLabel>
        {!tooLong && slots.length > 0 && (
          <div style={{ display: 'flex', gap: 6, background: 'var(--surface-2)', padding: 4, borderRadius: 11, marginBottom: 10 }}>
            {[['lista', 'Lista', 'list'], ['tidslinje', 'Tidslinje', 'chart']].map(([k, l, ic]) => (
              <button key={k} onClick={() => setView(k)} style={{ flex: 1, height: 36, borderRadius: 8, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: 700, fontSize: 13.5, background: view === k ? 'var(--surface)' : 'transparent', color: view === k ? 'var(--ink)' : 'var(--ink-3)', boxShadow: view === k ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}>
                <Icon name={ic} size={16} stroke={2} /> {l}
              </button>
            ))}
          </div>
        )}
        {tooLong ? (
          <Empty icon="clock" title="Ryms inte på en dag" sub={`Max ${WORK_LEN / 60} tim ryms inom en arbetsdag. Dela upp uppdraget.`} />
        ) : slots.length ? (
          view === 'tidslinje' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {slots.map(s => <DayTimeline key={s.iso} iso={s.iso} dayJobs={jobs.filter(j => j.date === s.iso)} durationMin={durationMin} settings={settings} onBook={onBook} />)}
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {slots.map((s, i) => {
              const dayLabel = s.iso === TODAY ? 'Idag' : s.iso === kAddDays(TODAY, 1) ? 'Imorgon' : kWeekdayLong(s.iso);
              const d = new Date(s.iso + 'T00:00:00');
              return (
                <Card key={s.iso} className="gv-fade" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 15px' }}>
                    <div style={{ width: 46, flexShrink: 0, textAlign: 'center' }}>
                      <div className="gv-tnum" style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1, color: 'var(--accent-ink)' }}>{d.getDate()}</div>
                      <div className="gv-mono" style={{ fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 3 }}>{K_MONTHS[d.getMonth()].slice(0, 3)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15.5, fontWeight: 720, display: 'flex', alignItems: 'center', gap: 7 }}>
                        {dayLabel}
                        {s.off && <span className="gv-badge is-sm" style={{ background: 'var(--surface-3)', color: 'var(--ink-2)' }}>{isWeekend(s.iso) ? 'Helg' : 'Röd dag'}</span>}
                      </div>
                      <div className="gv-tnum" style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                        {hm(s.start)}–{hm(s.end)} · {s.booked ? `${s.booked} inbokade` : 'helt ledig dag'}
                      </div>
                      {s.travel && <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--accent-ink)', fontWeight: 650, marginTop: 3 }}><Icon name="car" size={13} stroke={2} /> {s.travelMin} min restid inräknad efter föregående</div>}
                      {s.lunchMin > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--accent-ink)', fontWeight: 650, marginTop: 3 }}><Icon name="sun" size={13} stroke={2} /> Inkl. {s.lunchMin} min lunch under passet</div>}
                    </div>
                    <Btn variant="primary" size="sm" icon="plus" onClick={() => onBook(s.iso, hm(s.start))}>Boka</Btn>
                  </div>
                </Card>
              );
            })}
          </div>
          )
        ) : (
          <Empty icon="calendar" title="Ingen ledig lucka" sub={includeOff ? 'Fullbokat de närmaste veckorna.' : 'Inga vardagsluckor — prova att inkludera helger.'} />
        )}
      </div>
    </>
  );
}

export { CalendarScreen, FreeSlotsScreen, DayTimeline, SlotPickerSheet, holidaySet, isRedDay, isWeekend, findFreeSlots, placeInGap };

// ── SLOT PICKER (reschedule / follow-up) ────────────────────
// Suggests free slots for a duration (like the finder) and lets you override
// onto any date/time — even stacking more onto a single day.
function SlotPickerSheet({ open, onClose, title = 'Omplanera', subtitle, jobs = [], settings = {}, excludeId, defaultHours = 2, onPick, ctaLabel = 'Välj tid' }) {
  const [hours, setHours] = useStateK(defaultHours);
  const [includeOff, setIncludeOff] = useStateK(false);
  const [override, setOverride] = useStateK(false);
  const [ovDate, setOvDate] = useStateK(TODAY);
  const [ovTime, setOvTime] = useStateK('08:00');
  const [ovCal, setOvCal] = useStateK(false);

  useEffectK(() => { if (open) { setHours(defaultHours); setOverride(false); setOvDate(TODAY); setOvTime('08:00'); } }, [open, defaultHours]);

  const dur = Math.round(hours * 60);
  const otherJobs = useMemoK(() => excludeId ? jobs.filter(j => j.id !== excludeId) : jobs, [jobs, excludeId]);
  const slots = useMemoK(() => (dur > WORK_LEN ? [] : findFreeSlots(otherJobs, dur, includeOff, settings, 45, 8)), [otherJobs, dur, includeOff, settings]);

  const pick = (iso, startHM) => { onPick && onPick(iso, startHM, hours); };

  return (
    <Sheet open={open} onClose={onClose} title={title} maxH="90%">
      {subtitle && <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 12, marginTop: -2 }}>{subtitle}</div>}

      <div className="gv-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="clock" size={19} stroke={1.9} color="var(--ink-2)" />
          <span style={{ fontSize: 14.5, fontWeight: 650 }}>Tidsåtgång · {num(hours, hours % 1 ? 1 : 0)} tim</span>
        </div>
        <Stepper value={hours} onChange={(v) => setHours(Math.max(0.5, v))} step={0.5} min={0.5} size="sm" />
      </div>

      {!override ? (
        <>
          <div className="gv-lrow" style={{ padding: '4px 2px 10px', justifyContent: 'space-between', borderBottom: 'none' }}>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 600 }}>Inkludera helger & röda dagar</span>
            <Toggle on={includeOff} onChange={setIncludeOff} />
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)', margin: '4px 0 9px' }}>Föreslagna lediga tider</div>
          {dur > WORK_LEN ? (
            <Empty icon="clock" title="Ryms inte på en dag" sub="Dela upp uppdraget på flera dagar." />
          ) : slots.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
              {slots.map(s => {
                const dayLabel = s.iso === TODAY ? 'Idag' : s.iso === kAddDays(TODAY, 1) ? 'Imorgon' : kWeekdayLong(s.iso);
                const d = new Date(s.iso + 'T00:00:00');
                return (
                  <div key={s.iso} className="gv-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px' }}>
                    <div style={{ width: 42, flexShrink: 0, textAlign: 'center' }}>
                      <div className="gv-tnum" style={{ fontSize: 19, fontWeight: 800, lineHeight: 1, color: 'var(--accent-ink)' }}>{d.getDate()}</div>
                      <div className="gv-mono" style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 2 }}>{K_MONTHS[d.getMonth()].slice(0, 3)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {dayLabel}
                        {s.off && <span className="gv-badge is-sm" style={{ background: 'var(--surface-3)', color: 'var(--ink-2)' }}>{isWeekend(s.iso) ? 'Helg' : 'Röd dag'}</span>}
                      </div>
                      <div className="gv-tnum" style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 1 }}>{hm(s.start)}–{hm(s.end)} · {s.booked ? `${s.booked} inbokade` : 'ledig dag'}</div>
                      {(s.travel || s.lunchMin > 0) && <div style={{ fontSize: 11, color: 'var(--accent-ink)', fontWeight: 600, marginTop: 2 }}>{[s.travel ? `${s.travelMin} min restid` : null, s.lunchMin > 0 ? `${s.lunchMin} min lunch` : null].filter(Boolean).join(' · ')} inräknad</div>}
                    </div>
                    <Btn variant="primary" size="sm" icon="check" onClick={() => pick(s.iso, hm(s.start))}>Välj</Btn>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty icon="calendar" title="Ingen ledig lucka" sub={includeOff ? 'Fullbokat framåt.' : 'Prova att inkludera helger.'} />
          )}
          <button onClick={() => setOverride(true)} className="gv-card" style={{ width: '100%', marginTop: 4, padding: 12, border: '1.5px dashed var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--ink-2)', fontWeight: 650, fontSize: 13.5, background: 'var(--surface)' }}>
            <Icon name="calendar" size={17} stroke={1.9} /> Välj datum själv (kan boka fullt)
          </button>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ink-2)', background: 'var(--surface-2)', borderRadius: 10, padding: '9px 12px', marginBottom: 12 }}>
            <Icon name="calendar" size={15} stroke={2} color="var(--ink-3)" /> Lägg på valfri dag – även om den redan är fullbokad.
          </div>
          <span className="gv-label" style={{ display: 'block', marginBottom: 7 }}>Datum</span>
          <button onClick={() => setOvCal(true)} className="gv-chip" style={{ width: '100%', justifyContent: 'flex-start', gap: 8, height: 44, marginBottom: 12 }}>
            <Icon name="calendar" size={16} stroke={2} /> {fmtDateLong(ovDate)}
          </button>
          <div style={{ maxWidth: 180 }}><Field label="Starttid" value={ovTime} onChange={setOvTime} mono /></div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <Btn variant="secondary" onClick={() => setOverride(false)} style={{ flex: 1 }}>Tillbaka</Btn>
            <Btn variant="primary" icon="check" onClick={() => pick(ovDate, ovTime)} style={{ flex: 1.4 }}>{ctaLabel}</Btn>
          </div>
          <CalendarSheet open={ovCal} onClose={() => setOvCal(false)} value={ovDate} onSelect={setOvDate} minISO={TODAY} />
        </>
      )}
    </Sheet>
  );
}
